import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Sparkles, X, Send, ArrowLeft, BookOpen, BarChart3, LifeBuoy,
  ChevronRight, Plus, CheckCircle2, Loader2, Bot, Mail, Phone, User as UserIcon,
} from "lucide-react";
import { chatApp, chatPublic } from "@/api/assistant";
import {
  createTicket, listMyTickets, getMyTicket,
  listMyTicketMessages, postMyTicketMessage, reopenMyTicket, closeMyTicket,
  deleteMyTicketMessage,
} from "@/api/support";
import useSupportUnread from "@/hooks/useSupportUnread";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/cn";
import { isValidEmail, isValidPhone } from "@/lib/validation";
import SupportChat from "./SupportChat";
import ExpandableText from "@/components/ui/ExpandableText";

const STATUS_BADGE = {
  open: "bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300",
  in_progress: "bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300",
  resolved: "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300",
  closed: "bg-slate-200 text-slate-500 dark:bg-white/10 dark:text-slate-400",
};
const STATUS_LABEL = { open: "Open", in_progress: "In progress", resolved: "Resolved", closed: "Closed" };

const SUGGESTIONS = {
  public: ["What is AI-QA-Engine?", "How does it work?", "What plans do you offer?"],
  help: ["How do I run a test?", "How do I crawl a project?", "How do I invite a tester?"],
  data: ["What bugs did I find?", "Show my recent runs", "List my projects"],
};

// ── Bits ──────────────────────────────────────────────────────────────────────
function BotAvatar({ size = 36, glow = false }) {
  return (
    <span
      className="relative grid shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 via-violet-500 to-fuchsia-500 text-white shadow-soft"
      style={{ width: size, height: size }}
    >
      <Bot size={size * 0.52} />
      {glow && (
        <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-400 dark:border-ink-900" />
      )}
    </span>
  );
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-1 py-1.5">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-brand-400"
          animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </div>
  );
}

function MenuCard({ icon: Icon, tint, label, desc, onClick, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 text-left transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-soft dark:border-white/10 dark:bg-white/5 dark:hover:border-brand-500/40"
    >
      <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl transition-transform group-hover:scale-105", tint)}>
        <Icon size={18} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-800 dark:text-white">{label}</span>
        <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{desc}</span>
      </span>
      {badge}
      <ChevronRight size={16} className="shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-brand-400" />
    </button>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 420, damping: 30 }}
      className={cn("flex items-end gap-2", isUser ? "justify-end" : "justify-start")}
    >
      {!isUser && <BotAvatar size={26} />}
      <div
        className={cn(
          "max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2.5 text-[0.84375rem] leading-relaxed shadow-sm",
          isUser
            ? "rounded-br-md bg-gradient-to-br from-brand-600 to-violet-600 text-white"
            : "rounded-bl-md bg-white text-slate-700 ring-1 ring-slate-200 dark:bg-ink-800 dark:text-slate-200 dark:ring-white/10"
        )}
      >
        {content}
      </div>
    </motion.div>
  );
}

// ── Main widget ───────────────────────────────────────────────────────────────
export default function Assistant({ mode = "app" }) {
  const isPublic = mode === "public";
  const { user, isAuthenticated } = useAuth();

  // Public widget only makes sense unauthenticated; the in-app one needs a session.
  const enabled = isPublic ? true : isAuthenticated;

  const [open, setOpen] = useState(false);
  const [view, setView] = useState("home"); // home | chat | support | tickets | ticket
  const [botMode, setBotMode] = useState(null); // help | data
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const [tickets, setTickets] = useState({ active: [], recentClosed: [] });
  const [activeTicketId, setActiveTicketId] = useState(null);
  const [activeTicket, setActiveTicket] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const { count: unreadCount, refresh: refreshUnread } = useSupportUnread();

  // Seed support contact fields from the signed-in user.
  useEffect(() => {
    if (isPublic || !user) return;
    setForm((p) => ({
      ...p,
      name: p.name || user.name || "",
      email: p.email || user.email || "",
    }));
  }, [isPublic, user]);

  const refreshTickets = useCallback(async () => {
    if (isPublic) return;
    try {
      const d = await listMyTickets();
      setTickets({ active: d.active || [], recentClosed: d.recentClosed || [] });
    } catch {
      /* keep stale */
    }
  }, [isPublic]);

  useEffect(() => {
    if (!open || isPublic) return undefined;
    refreshTickets();
    const id = setInterval(refreshTickets, 30000);
    return () => clearInterval(id);
  }, [open, isPublic, refreshTickets]);

  // Load ticket metadata (status) when viewing a ticket.
  useEffect(() => {
    if (view !== "ticket" || !activeTicketId || isPublic) return undefined;
    let cancelled = false;
    setTicketLoading(true);
    getMyTicket(activeTicketId)
      .then((t) => !cancelled && setActiveTicket(t))
      .catch(() => !cancelled && setActiveTicket(null))
      .finally(() => !cancelled && setTicketLoading(false));
    const id = setInterval(() => {
      getMyTicket(activeTicketId).then((t) => !cancelled && setActiveTicket(t)).catch(() => {});
    }, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [view, activeTicketId, isPublic]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, loading]);

  useEffect(() => {
    if (open && view === "chat") setTimeout(() => inputRef.current?.focus(), 120);
  }, [open, view]);

  // Let anything on the page (e.g. footer / pricing "Contact us") open this
  // widget straight to the support form via a global event.
  useEffect(() => {
    const onContact = () => {
      setSent(false);
      setForm({ name: user?.name || "", phone: "", email: user?.email || "", message: "" });
      setView("support");
      setOpen(true);
    };
    window.addEventListener("aiqa:contact", onContact);
    return () => window.removeEventListener("aiqa:contact", onContact);
  }, [user]);

  // ── Actions ──
  const goHome = () => { setView("home"); setBotMode(null); setErr(null); setActiveTicket(null); };
  const startChat = (m) => { setBotMode(m); setHistory([]); setErr(null); setView("chat"); };
  const openSupport = () => {
    setSent(false);
    setForm({ name: user?.name || "", phone: "", email: user?.email || "", message: "" });
    setView("support");
  };
  const openTicket = (id) => { setActiveTicketId(id); setView("ticket"); };

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || loading) return;
    const priorHistory = history;
    setHistory((h) => [...h, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const data = isPublic
        ? await chatPublic(text, priorHistory)
        : await chatApp(text, priorHistory, botMode || "");
      let reply = data.reply || "";
      const needsHelp = reply.includes("[NEED_HELP]");
      reply = reply.replace(/\s*\[NEED_HELP\]\s*/g, "").trim();
      setHistory((h) => [...h, { role: "assistant", content: reply || "…" }]);
      if (needsHelp) {
        setTimeout(() => {
          setHistory((h) => [...h, {
            role: "assistant",
            content: "I couldn't fully answer that. Would you like to talk to our support team?",
            showSupport: true,
          }]);
        }, 450);
      }
    } catch {
      setHistory((h) => [...h, { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // Anonymous (public) reporters must give a valid email — it's the only way
  // support can reply, since they have no account to read replies in. A
  // signed-in reporter may leave it blank (their account email is used
  // instead), but if they typed one it still has to be a real address —
  // that check must run regardless of isPublic, not only when !isPublic.
  const emailOk = form.email.trim() ? isValidEmail(form.email) : !isPublic;
  const phoneOk = !form.phone.trim() || isValidPhone(form.phone);
  const supportReady = !!form.name.trim() && !!form.message.trim() && emailOk && phoneOk;

  const submitSupport = async () => {
    if (!supportReady || sending) return;
    setSending(true);
    setErr(null);
    try {
      const res = await createTicket({ ...form, source: isPublic ? "landing_page" : "chatbot" });
      setSent(true);
      if (!isPublic && res?.id) {
        await refreshTickets();
        setTimeout(() => openTicket(res.id), 700);
      }
    } catch (e) {
      // The backend sends actionable text here (rate limit wait, invalid email,
      // missing fields) — showing a generic retry line makes the user retry into
      // the same wall.
      setErr((e && e.message) || "Failed to submit. Please try again.");
    } finally {
      setSending(false);
    }
  };

  // Stable identity so SupportChat's load callback / poll interval don't reset
  // on every render of this widget.
  const ticketsApi = useMemo(() => ({
    listMessages: (id) => listMyTicketMessages(id),
    postMessage: async (id, body) => {
      const r = await postMyTicketMessage(id, body);
      refreshUnread();
      refreshTickets();
      return r;
    },
    // The reporter may remove a message they sent themselves, on their own
    // open ticket. SupportChat shows the control on exactly those, and the
    // backend re-checks both facts before it deletes anything.
    deleteMessage: async (id, messageId) => {
      const r = await deleteMyTicketMessage(id, messageId);
      refreshTickets();
      return r;
    },
  }), [refreshUnread, refreshTickets]);

  const ticketLocked = activeTicket?.status === "closed";
  const ticketResolved = activeTicket?.status === "resolved";
  const ticketActive = activeTicket?.status === "open" || activeTicket?.status === "in_progress";
  const headerTitle =
    view === "support" ? "Contact Support"
      : view === "tickets" ? "Your tickets"
        : view === "ticket" ? `Ticket #${(activeTicketId || "").slice(0, 8)}`
          : botMode === "data" ? "My Data"
            : botMode === "help" ? "Help Guide"
              : "Iris Assistant";
  const headerSub =
    view === "home" ? "How can I help you today?"
      : view === "support" ? "We'll reply right here, live"
        : view === "tickets" ? `${tickets.active.length} active`
          : view === "ticket" ? "Replies appear live"
            : botMode === "data" ? "Answers from your live data"
              : "Powered by AI-QA-Engine";

  if (!enabled) return null;

  return (
    <>
      {/* ── Floating button ──
          Hidden on mobile while the panel is open: the panel goes full-screen
          there (max-sm:inset-0) at a LOWER z-index, so this launcher floated on
          top of it, covering the composer in the bottom-right corner. The panel
          header has its own close button. */}
      <div className={cn("fixed bottom-5 right-5 z-[70] print:hidden", open && "max-sm:hidden")}>
        {!open && (
          <motion.span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-500 to-fuchsia-500"
            animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
          />
        )}
        <motion.button
          type="button"
          onClick={() => setOpen((o) => !o)}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          aria-label={open ? "Close assistant" : "Open AI assistant"}
          className="relative grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 via-violet-500 to-fuchsia-500 text-white shadow-glow"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={open ? "close" : "open"}
              initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              exit={{ rotate: 90, opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.18 }}
            >
              {open ? <X size={22} /> : <Sparkles size={22} />}
            </motion.span>
          </AnimatePresence>
          {!open && !isPublic && unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full border-2 border-white bg-rose-500 px-1 text-[0.625rem] font-bold text-white dark:border-ink-950">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </motion.button>
      </div>

      {/* ── Panel ── */}
      {/* Inertness from LIVE state on this wrapper. The panel is full-screen on
          mobile (max-sm:inset-0), so if its exit animation ever failed to
          complete it would sit invisibly over the entire app and swallow every
          tap — the exact failure the mobile nav drawer used to have. An
          AnimatePresence child is re-rendered with STALE props while exiting, so
          the guard has to live on a wrapper that is not exiting. */}
      <div className={cn(!open && "pointer-events-none")}>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 360, damping: 30 }}
            className="fixed z-[69] flex flex-col overflow-hidden border border-slate-200 bg-white/90 shadow-glow backdrop-blur-xl dark:border-white/10 dark:bg-ink-950/90
                       bottom-24 right-5 h-[clamp(440px,72vh,640px)] w-[min(25rem,calc(100vw-2.5rem))] rounded-3xl
                       max-sm:inset-0 max-sm:bottom-0 max-sm:right-0 max-sm:h-full max-sm:w-full max-sm:rounded-none"
          >
            {/* Header */}
            <div className="relative flex items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-white/10">
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-brand-500/10 via-violet-500/5 to-transparent" />
              <BotAvatar size={38} glow />
              <div className="relative min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-bold text-slate-800 dark:text-white">{headerTitle}</span>
                  {view === "chat" && (
                    <span className={cn("rounded-md px-1.5 py-0.5 text-[0.5625rem] font-bold uppercase tracking-wide",
                      botMode === "data" ? "bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300" : "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300")}>
                      {botMode === "data" ? "Live data" : "Guide"}
                    </span>
                  )}
                  {view === "ticket" && activeTicket && (
                    <span className={cn("rounded-md px-1.5 py-0.5 text-[0.5625rem] font-bold uppercase tracking-wide", STATUS_BADGE[activeTicket.status])}>
                      {STATUS_LABEL[activeTicket.status]}
                    </span>
                  )}
                </div>
                <p className="truncate text-[0.6875rem] text-slate-500 dark:text-slate-400">{headerSub}</p>
              </div>
              <div className="relative flex items-center gap-1">
                {view !== "home" && (
                  <button type="button" onClick={goHome} aria-label="Back" className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10">
                    <ArrowLeft size={16} />
                  </button>
                )}
                <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10">
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="relative flex-1 overflow-hidden">
              <AnimatePresence mode="wait">
                {/* HOME */}
                {view === "home" && (
                  <motion.div
                    key="home"
                    initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }}
                    transition={{ duration: 0.18 }}
                    className="absolute inset-0 flex flex-col gap-2.5 overflow-y-auto p-4"
                  >
                    <div className="mb-1 flex flex-col items-center text-center">
                      <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>
                        <BotAvatar size={52} />
                      </motion.div>
                      <h3 className="mt-3 text-base font-bold text-slate-800 dark:text-white">{"Hi, I'm Iris 👋"}</h3>
                      <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Your AI-QA-Engine assistant.</p>
                    </div>
                    {!isPublic && (
                      <MenuCard icon={BarChart3} tint="bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300"
                        label="My Data" desc="Runs, bugs, projects & saved tests" onClick={() => startChat("data")} />
                    )}
                    <MenuCard icon={BookOpen} tint="bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300"
                      label={isPublic ? "How it works" : "Help Guide"} desc="Features & step-by-step how-tos" onClick={() => startChat("help")} />
                    <MenuCard icon={LifeBuoy} tint="bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300"
                      label={isPublic ? "Contact us" : "Contact Support"} desc="Open a ticket — chat with a human" onClick={openSupport} />
                    {!isPublic && tickets.active.length > 0 && (
                      <MenuCard icon={LifeBuoy} tint="bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300"
                        label="Your tickets" desc={`${tickets.active.length} active`} onClick={() => setView("tickets")}
                        badge={unreadCount > 0 && (
                          <span className="grid h-5 min-w-5 place-items-center rounded-full bg-rose-500 px-1 text-[0.625rem] font-bold text-white">{unreadCount}</span>
                        )} />
                    )}
                    <p className="mt-auto pt-2 text-center text-[0.625rem] font-semibold uppercase tracking-wider text-slate-300 dark:text-slate-600">
                      AI-QA-Engine
                    </p>
                  </motion.div>
                )}

                {/* CHAT */}
                {view === "chat" && (
                  <motion.div
                    key="chat"
                    initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }}
                    transition={{ duration: 0.18 }}
                    className="absolute inset-0 flex flex-col"
                  >
                    <div className="flex-1 space-y-3 overflow-y-auto p-4">
                      {history.length === 0 && (
                        <div className="flex flex-col items-center gap-3 px-4 py-6 text-center">
                          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-brand-500/15 to-violet-500/10 text-brand-500">
                            {botMode === "data" ? <BarChart3 size={22} /> : <BookOpen size={22} />}
                          </span>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {botMode === "data" ? "Ask about your runs, bugs, projects or saved tests." : "Ask how to use any feature of the platform."}
                          </p>
                          <div className="flex flex-wrap justify-center gap-2">
                            {(SUGGESTIONS[isPublic ? "public" : botMode] || []).map((s) => (
                              <button key={s} type="button" onClick={() => send(s)}
                                className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[0.71875rem] text-slate-600 transition hover:border-brand-300 hover:text-brand-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                                {s}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      {history.map((m, i) => (
                        <div key={i} className="space-y-2">
                          <Bubble role={m.role} content={m.content} />
                          {m.showSupport && (
                            <div className="pl-9">
                              <button type="button" onClick={openSupport}
                                className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-br from-brand-600 to-violet-600 px-3.5 py-2 text-xs font-semibold text-white shadow-soft transition hover:brightness-110">
                                <LifeBuoy size={14} /> Contact Support
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                      {loading && (
                        <div className="flex items-end gap-2">
                          <BotAvatar size={26} />
                          <div className="rounded-2xl rounded-bl-md bg-white px-2 py-1 ring-1 ring-slate-200 dark:bg-ink-800 dark:ring-white/10">
                            <TypingDots />
                          </div>
                        </div>
                      )}
                      <div ref={bottomRef} />
                    </div>
                    <div className="flex items-end gap-2 border-t border-slate-200 bg-white/70 p-2.5 dark:border-white/10 dark:bg-white/5">
                      <textarea
                        ref={inputRef} rows={1} value={input}
                        onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
                        placeholder="Type your message…"
                        className="max-h-24 flex-1 resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-[0.84375rem] text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-brand-400 dark:border-white/10 dark:bg-ink-900 dark:text-slate-100"
                      />
                      <button type="button" onClick={() => send()} disabled={!input.trim() || loading} aria-label="Send"
                        className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-600 to-violet-600 text-white shadow-soft transition enabled:hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40">
                        {loading ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* SUPPORT FORM */}
                {view === "support" && (
                  <motion.div
                    key="support"
                    initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }}
                    transition={{ duration: 0.18 }}
                    className="absolute inset-0 overflow-y-auto p-4"
                  >
                    {sent ? (
                      <div className="flex flex-col items-center px-4 py-10 text-center">
                        <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 18 }}
                          className="grid h-14 w-14 place-items-center rounded-2xl bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                          <CheckCircle2 size={28} />
                        </motion.span>
                        <h4 className="mt-4 text-base font-bold text-slate-800 dark:text-white">Ticket created!</h4>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {isPublic ? "Our team will reach out soon." : "Opening your live chat…"}
                        </p>
                        {isPublic && (
                          <button type="button" onClick={goHome} className="mt-5 rounded-xl bg-gradient-to-br from-brand-600 to-violet-600 px-5 py-2 text-xs font-semibold text-white shadow-soft">
                            Back to menu
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="mb-3 flex items-center gap-2.5 rounded-xl bg-brand-50 px-3.5 py-2.5 text-[0.78125rem] text-brand-700 ring-1 ring-brand-100 dark:bg-brand-500/10 dark:text-brand-200 dark:ring-brand-500/20">
                          <LifeBuoy size={16} className="shrink-0" />
                          {isPublic ? "Tell us how we can help and we'll get back to you." : "Create a ticket — replies appear right here, live."}
                        </div>
                        {[
                          { k: "name", label: "Name", icon: UserIcon, type: "text", req: true },
                          {
                            k: "email", label: "Email", icon: Mail, type: "email", req: isPublic,
                            error: form.email.trim() && !isValidEmail(form.email) ? "Enter a valid email address." : "",
                          },
                          {
                            k: "phone", label: "Phone", icon: Phone, type: "tel",
                            error: form.phone.trim() && !isValidPhone(form.phone) ? "Enter a valid phone number." : "",
                          },
                        ].map((f) => (
                          <div key={f.k} className="mb-2.5">
                            <label className="mb-1 block text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-400">
                              {f.label}{f.req && <span className="text-rose-500"> *</span>}
                            </label>
                            <div className="relative">
                              <f.icon size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                              <input type={f.type} value={form[f.k]} onChange={(e) => setForm((s) => ({ ...s, [f.k]: e.target.value }))}
                                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-[0.8125rem] text-slate-800 outline-none transition-colors focus:border-brand-400 dark:border-white/10 dark:bg-ink-900 dark:text-slate-100" />
                            </div>
                            {f.error && <p className="mt-1 text-[0.6875rem] text-rose-500">{f.error}</p>}
                          </div>
                        ))}
                        <div className="mb-3">
                          <label className="mb-1 block text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-400">
                            Message<span className="text-rose-500"> *</span>
                          </label>
                          <textarea rows={3} value={form.message} onChange={(e) => setForm((s) => ({ ...s, message: e.target.value }))}
                            placeholder="Describe your issue…"
                            className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[0.8125rem] text-slate-800 outline-none transition-colors focus:border-brand-400 dark:border-white/10 dark:bg-ink-900 dark:text-slate-100" />
                        </div>
                        {err && <p className="mb-2 text-xs text-rose-500">{err}</p>}
                        <button type="button" onClick={submitSupport} disabled={!supportReady || sending}
                          className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-brand-600 to-violet-600 py-2.5 text-sm font-semibold text-white shadow-soft transition enabled:hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40">
                          {sending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
                          {sending ? "Submitting…" : "Create ticket"}
                        </button>
                      </>
                    )}
                  </motion.div>
                )}

                {/* TICKETS LIST */}
                {view === "tickets" && (
                  <motion.div
                    key="tickets"
                    initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }}
                    transition={{ duration: 0.18 }}
                    className="absolute inset-0 space-y-2 overflow-y-auto p-4"
                  >
                    {tickets.active.length === 0 && tickets.recentClosed.length === 0 ? (
                      <p className="py-10 text-center text-xs text-slate-400">{"You haven't raised any tickets yet."}</p>
                    ) : (
                      <>
                        {[["Active", tickets.active], ["Recently closed", tickets.recentClosed]].map(([title, list]) =>
                          list.length > 0 && (
                            <div key={title} className="space-y-2">
                              <p className="px-1 pt-1 text-[0.6875rem] font-bold uppercase tracking-wider text-slate-400">{title}</p>
                              {list.map((t) => (
                                <button key={t.id} type="button" onClick={() => openTicket(t.id)}
                                  className="flex w-full items-center gap-2.5 rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:border-brand-300 dark:border-white/10 dark:bg-white/5">
                                  <span className={cn("rounded-md px-1.5 py-0.5 text-[0.5625rem] font-bold uppercase", STATUS_BADGE[t.status])}>{STATUS_LABEL[t.status]}</span>
                                  <span className="min-w-0 flex-1">
                                    <span className="block truncate text-xs text-slate-700 dark:text-slate-200">#{t.id.slice(0, 8)} · {t.message?.slice(0, 40)}</span>
                                    <span className="block text-[0.625rem] text-slate-400">{t.messageCount} message{t.messageCount === 1 ? "" : "s"}</span>
                                  </span>
                                  {t.unreadCount > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-rose-500 px-1 text-[0.625rem] font-bold text-white">{t.unreadCount}</span>}
                                </button>
                              ))}
                            </div>
                          )
                        )}
                      </>
                    )}
                    <button type="button" onClick={openSupport}
                      className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-brand-600 to-violet-600 py-2.5 text-xs font-semibold text-white shadow-soft">
                      <Plus size={14} /> New ticket
                    </button>
                  </motion.div>
                )}

                {/* TICKET CHAT */}
                {view === "ticket" && (
                  <motion.div
                    key="ticket"
                    initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }}
                    transition={{ duration: 0.18 }}
                    className="absolute inset-0 space-y-3 overflow-y-auto p-3.5"
                  >
                    {ticketLoading && !activeTicket ? (
                      <div className="grid place-items-center py-10 text-slate-400"><Loader2 className="animate-spin" size={18} /></div>
                    ) : !activeTicket ? (
                      <p className="py-10 text-center text-xs text-slate-400">Could not load this ticket.</p>
                    ) : (
                      <>
                        {activeTicket.message && (
                          <div data-probe="request" className="min-w-0 rounded-xl bg-slate-100 px-3 py-2.5 text-xs text-slate-600 dark:bg-white/5 dark:text-slate-300">
                            <p className="mb-1 text-[0.625rem] font-bold uppercase tracking-wide text-slate-400">Original request</p>
                            {/* Same renderer as the SuperAdmin inbox, so the two
                                views of one request can't drift apart. This panel
                                is the narrower of the two, which makes an
                                unwrapped long request worse here, not better:
                                it used to be dropped in raw, with no wrapping
                                rules, no line breaks preserved and no height
                                bound at all. */}
                            <ExpandableText
                              text={activeTicket.message}
                              previewLines={4}
                              expandable
                              moreLabel="View full request"
                              className="text-xs"
                            />
                          </div>
                        )}
                        <SupportChat ticket={activeTicket} role="user" api={ticketsApi} locked={ticketLocked} />
                        {ticketLocked && (
                          <div className="rounded-xl bg-slate-100 p-3 text-center dark:bg-white/5">
                            <p className="mb-2 text-xs text-slate-600 dark:text-slate-300">This ticket is closed.</p>
                            <button type="button" onClick={openSupport} className="rounded-lg bg-gradient-to-br from-brand-600 to-violet-600 px-3.5 py-1.5 text-xs font-semibold text-white">Start a new ticket</button>
                          </div>
                        )}
                        {ticketResolved && !ticketLocked && (
                          <button type="button" onClick={async () => { try { setActiveTicket(await reopenMyTicket(activeTicket.id)); } catch { /* ignore */ } }}
                            className="w-full rounded-lg border border-slate-200 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5">
                            Reopen ticket
                          </button>
                        )}
                        {ticketActive && (
                          <button type="button"
                            onClick={async () => {
                              try {
                                setActiveTicket(await closeMyTicket(activeTicket.id));
                                refreshTickets();
                              } catch { /* ignore */ }
                            }}
                            className="w-full rounded-lg border border-slate-200 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5">
                            Close ticket
                          </button>
                        )}
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </>
  );
}
